// src/components/dashboard/NetZeroBalance2050Card.js
import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  useDecompScene2050ByScenario,
  labelForScenarioKey,
} from "../../hooks/useDecompScene2050ByScenario";
import { getAbatementColor } from "../../config/colorScheme";

// Only these wedges are rendered
const ORDER = [
  "Other",
  "Electrification",
  "Energy demand reduction",
  "Carbon dioxide removals",
];
const ALLOWED = new Set(ORDER);

const COLORS = {
  Other: { line: "#6b7280", fill: "#6b728033" },
  Electrification: { line: "#2563eb", fill: "#2563eb33" },
  "Energy demand reduction": { line: "#16a34a", fill: "#16a34a33" },
  "Carbon dioxide removals": { line: "#a855f7", fill: "#a855f733" },
};

function canon(w) {
  if (!w) return "";
  const s = String(w).trim().toLowerCase();
  if (s.startsWith("other")) return "Other";
  if (s.startsWith("electr")) return "Electrification";
  if (s.startsWith("energy demand")) return "Energy demand reduction";
  if (s.startsWith("carbon dioxide remov")) return "Carbon dioxide removals";
  if (s.startsWith("remaining emissions")) return "";
  if (s.startsWith("electricity generation")) return "";
  if (s.startsWith("removals")) return "";
  return "";
}

export default function NetZeroBalance2050Card({
  // switched to cumulative-only; expose window if you want to tweak it
  start = 2025,
  end = 2050,
  scenario,                // required
  comparisonScenario = null,
  title = "The Net Zero Balance (cumulative)",
  subtitle = "Share of the 2025–2050 net balance",
  yAxisTitle = "% of cumulative balance",
  unitScale = 1,
  unitSuffix = "",
  valueFormatter = (v) => v?.toLocaleString?.() ?? v,
}) {
  // CUMULATIVE pulls only
 const a = useDecompScene2050ByScenario({
  scenario,
  start: 2025,
  end: 2050,
  roundToWholePercent: true, // set true to match Steve’s whole-% totals
});
 const b = useDecompScene2050ByScenario({
  scenario: comparisonScenario || undefined,
  start: 2025,
  end: 2050,
  roundToWholePercent: true,
});

  const xLabels = useMemo(() => {
    const labels = [labelForScenarioKey(scenario)];
    if (comparisonScenario) labels.push(labelForScenarioKey(comparisonScenario));
    return labels;
  }, [scenario, comparisonScenario]);

  // Canonicalise + filter
  const aMap = useMemo(() => {
    const m = {};
    for (const [w, v] of Object.entries(a.byWedge || {})) {
      const k = canon(w);
      if (k && ALLOWED.has(k)) m[k] = v;
    }
    return m;
  }, [a.byWedge]);

  const bMap = useMemo(() => {
    if (!comparisonScenario) return null;
    const m = {};
    for (const [w, v] of Object.entries(b.byWedge || {})) {
      const k = canon(w);
      if (k && ALLOWED.has(k)) m[k] = v;
    }
    return m;
  }, [b.byWedge, comparisonScenario]);

  const wedges = useMemo(() => {
    const present = new Set([
      ...Object.keys(aMap || {}),
      ...(bMap ? Object.keys(bMap) : []),
    ]);
    return ORDER.filter((k) => present.has(k));
  }, [aMap, bMap]);

  // Positive percentages (sum to 100)
  const datasets = useMemo(() => {
    const drawOrder = {
      "Carbon dioxide removals": 0,
      "Energy demand reduction": 1,
      Electrification: 2,
      Other: 3,
    };

    const aTotal = wedges.reduce(
      (s, w) => s + Math.abs((aMap[w] ?? 0) * unitScale),
      0
    );
    const bTotal = comparisonScenario
      ? wedges.reduce(
          (s, w) => s + Math.abs((bMap?.[w] ?? 0) * unitScale),
          0
        )
      : null;

    return wedges.map((wedge) => {
      const color = getAbatementColor(wedge, "primary") || "#999";
      const fillColor = getAbatementColor(wedge, "light") || "rgba(0,0,0,0.12)";

      const aAbs = Math.abs((aMap[wedge] ?? 0) * unitScale);
      const aPct = aTotal ? (aAbs / aTotal) * 100 : 0;

      let data;
      if (comparisonScenario) {
        const bAbs = Math.abs((bMap?.[wedge] ?? 0) * unitScale);
        const bPct = bTotal ? (bAbs / bTotal) * 100 : 0;
        data = [aPct, bPct];
      } else {
        data = [aPct];
      }

      return {
        label: wedge,
        data,
        backgroundColor: fillColor,
        borderColor: color,
        borderWidth: 1,
        stack: "nz-cum",
        order: drawOrder[wedge] ?? 99,
      };
    });
  }, [wedges, aMap, bMap, comparisonScenario, unitScale]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: { display: true, text: title, font: { size: 14 } },
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            padding: 12,
            sort: (a, b) => ORDER.indexOf(a.text) - ORDER.indexOf(b.text),
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            title: (items) => xLabels[items?.[0]?.dataIndex ?? 0],
            label: (ctx) => {
              const wedge = ctx.dataset.label;
              const isComp = ctx.dataIndex === 1 && !!comparisonScenario;
              const map = isComp ? bMap : aMap;
              const raw = (map?.[wedge] ?? 0) * unitScale;   // signed
              const pct = ctx.parsed?.y ?? 0;                // positive %
              const absStr = valueFormatter(Math.abs(raw));  // show absolute
              return `${wedge}: ${pct.toFixed(1)}% (|${absStr}|${unitSuffix})`;
            },
            footer: () => `Total: 100%`,
          },
        },
      },
      scales: {
        x: { stacked: true },
        y: {
          stacked: true,
          beginAtZero: true,
          suggestedMax: 100,
          title: { display: true, text: yAxisTitle },
          ticks: { callback: (v) => `${v}%` },
        },
      },
    }),
    [title, xLabels, yAxisTitle, valueFormatter, unitSuffix, comparisonScenario, aMap, bMap]
  );

  const loading = a.loading || (comparisonScenario && b.loading);
  const error = a.error || (comparisonScenario && b.error);

  return (
    <div className="bg-white p-4 rounded-lg flex flex-col items-center">
      <div className="mt-2 w-full flex flex-col items-center">
        <div className="text-sm mb-2 text-gray-600">
          {subtitle || `Share of the ${start}–${end} net balance`}
        </div>
        <div className="w-full h-96">
          {loading ? (
            <div className="w-full h-full grid place-items-center">
              Loading cumulative balance…
            </div>
          ) : error ? (
            <div className="w-full h-full grid place-items-center text-red-600">
              Error: {error.message}
            </div>
          ) : datasets.length === 0 ? (
            <div className="w-full h-full grid place-items-center text-gray-500">
              No data for {start}–{end}.
            </div>
          ) : (
            <Bar data={{ labels: xLabels, datasets }} options={options} />
          )}
        </div>
      </div>
    </div>
  );
}
